import * as ort from 'onnxruntime-web'
import type { ImageDataLike } from './ocr-types'

export interface TextLineRegion {
  x: number
  y: number
  width: number
  height: number
  lineNumber: number
}

export class OcrDbDetector {
  private session: ort.InferenceSession | null = null
  private initialized = false
  private readonly maxSide = 960
  private readonly threshold = 0.3
  private readonly boxThresh = 0.5
  private readonly unclipRatio = 1.6
  private readonly minBoxSize = 3

  private _resizedW = 0
  private _resizedH = 0

  get isInitialized(): boolean {
    return this.initialized
  }

  async initialize(modelBuffer: ArrayBuffer): Promise<void> {
    if (this.initialized) return

    ort.env.wasm.wasmPaths = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.25.1/dist/'
    ort.env.wasm.numThreads = 0

    this.session = await ort.InferenceSession.create(modelBuffer, {
      executionProviders: ['webgpu', 'webgl', 'wasm'],
      graphOptimizationLevel: 'all',
    })

    this.initialized = true
  }

  async detectLinesAsync(pageImage: ImageDataLike): Promise<TextLineRegion[]> {
    if (!this.session) return []

    const { tensor, ratioW, ratioH } = this.preprocess(pageImage)

    const inputName = this.session.inputNames[0]!
    const inputTensor = new ort.Tensor('float32', tensor, [1, 3, this._resizedH, this._resizedW])
    const feeds: Record<string, ort.Tensor> = {}
    feeds[inputName] = inputTensor

    const outputs = await this.session.run(feeds)
    const outputName = this.session.outputNames[0]!
    const probTensor = outputs[outputName]

    let probMap: Float32Array
    if (probTensor instanceof ort.Tensor) {
      probMap = new Float32Array(probTensor.data as Float32Array)
    } else {
      probMap = new Float32Array(probTensor as unknown as ArrayBuffer)
    }

    if ((window as any).__OCR_DEBUG__) {
      let activePx = 0
      let pmin = Infinity, pmax = -Infinity, psum = 0
      const sampleStep = Math.max(1, Math.floor(probMap.length / 500))
      let sampleCount = 0
      for (let i = 0; i < probMap.length; i += sampleStep) {
        const v = probMap[i] ?? 0
        if (v > this.threshold) activePx++
        if (v < pmin) pmin = v
        if (v > pmax) pmax = v
        psum += v
        sampleCount++
      }
      console.groupCollapsed(`[DB Detector] ${pageImage.width}x${pageImage.height} → ${this._resizedW}x${this._resizedH}`)
      console.log('Input shape:', `1×3×${this._resizedH}×${this._resizedW}`)
      console.log('Prob map stats:', {
        min: pmin.toFixed(4),
        max: pmax.toFixed(4),
        mean: (psum / sampleCount).toFixed(4),
        activeRatio: `${((activePx / sampleCount) * 100).toFixed(1)}%`,
      })
      console.groupEnd()
    }

    return this.postprocess(probMap, ratioW, ratioH)
  }

  private preprocess(pageImage: ImageDataLike) {
    const { data: pixels, width, height } = pageImage

    let newW = width
    let newH = height
    const maxDim = Math.max(width, height)
    if (maxDim > this.maxSide) {
      const scale = this.maxSide / maxDim
      newW = Math.round(width * scale)
      newH = Math.round(height * scale)
    }

    newW = Math.ceil(newW / 32) * 32
    newH = Math.ceil(newH / 32) * 32

    this._resizedW = newW
    this._resizedH = newH

    const ratioW = width / newW
    const ratioH = height / newH

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')!
    const imageData = new ImageData(new Uint8ClampedArray(pixels), width, height)
    ctx.putImageData(imageData, 0, 0)

    const targetCanvas = document.createElement('canvas')
    targetCanvas.width = newW
    targetCanvas.height = newH
    const targetCtx = targetCanvas.getContext('2d')!
    targetCtx.drawImage(canvas, 0, 0, newW, newH)
    const resizedData = targetCtx.getImageData(0, 0, newW, newH)

    const tensor = new Float32Array(3 * newH * newW)
    const meanR = 0.485, meanG = 0.456, meanB = 0.406
    const stdR = 0.229, stdG = 0.224, stdB = 0.225

    for (let i = 0; i < newH * newW; i++) {
      const r = (resizedData.data[i * 4] ?? 0) / 255.0
      const g = (resizedData.data[i * 4 + 1] ?? 0) / 255.0
      const b = (resizedData.data[i * 4 + 2] ?? 0) / 255.0

      tensor[0 * newH * newW + i] = (r - meanR) / stdR
      tensor[1 * newH * newW + i] = (g - meanG) / stdG
      tensor[2 * newH * newW + i] = (b - meanB) / stdB
    }

    return { tensor, ratioW, ratioH }
  }

  private postprocess(
    probMap: Float32Array,
    ratioW: number,
    ratioH: number,
  ): TextLineRegion[] {
    const h = this._resizedH
    const w = this._resizedW

    const bitmap = new Uint8Array(h * w)
    for (let i = 0; i < h * w; i++) {
      bitmap[i] = (probMap[i] ?? 0) > this.threshold ? 1 : 0
    }

    const labels = labelConnectedComponents(bitmap, w, h)
    let labelCount = 0
    for (let i = 0; i < labels.length; i++) {
      if ((labels[i] ?? 0) > labelCount) labelCount = labels[i]!
    }

    if (labelCount === 0) return []

    interface RawBox { x: number; y: number; width: number; height: number }
    const candidates: RawBox[] = []

    for (let lbl = 1; lbl <= labelCount; lbl++) {
      let minX = w, minY = h, maxX = 0, maxY = 0
      let pixelCount = 0

      for (let i = 0; i < labels.length; i++) {
        if (labels[i] === lbl) {
          const px = i % w
          const py = Math.floor(i / w)
          if (px < minX) minX = px
          if (px > maxX) maxX = px
          if (py < minY) minY = py
          if (py > maxY) maxY = py
          pixelCount++
        }
      }

      if (pixelCount < this.minBoxSize) continue
      candidates.push({ x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 })
    }

    const scored: { box: RawBox; score: number }[] = []

    for (const box of candidates) {
      const area = box.width * box.height
      const perimeter = 2 * (box.width + box.height)
      if (perimeter === 0) continue

      const distance = area * this.unclipRatio / perimeter

      const ux = Math.max(0, Math.floor(box.x - distance))
      const uy = Math.max(0, Math.floor(box.y - distance))
      const uw = Math.min(w - ux, Math.ceil(box.width + distance * 2))
      const uh = Math.min(h - uy, Math.ceil(box.height + distance * 2))

      if (uw <= 0 || uh <= 0) continue

      let sum = 0
      let count = 0
      for (let row = uy; row < Math.min(h, uy + uh); row++) {
        for (let col = ux; col < Math.min(w, ux + uw); col++) {
          const val = probMap[row * w + col] ?? 0
          if (val > this.threshold) {
            sum += val
            count++
          }
        }
      }

      if (count === 0) continue
      const score = sum / count

      if (score >= this.boxThresh) {
        scored.push({
          box: { x: ux, y: uy, width: uw, height: uh },
          score,
        })
      }
    }

    const padded: TextLineRegion[] = []

    for (const { box } of scored) {
      let px = Math.max(0, box.x - 2)
      let py = Math.max(0, box.y - 2)
      let pw = Math.min(w - px, box.width + 4)
      let ph = Math.min(h - py, box.height + 4)

      for (const other of scored) {
        if (other.box === box) continue
        const a = { x: px, y: py, width: pw, height: ph }
        const b = other.box
        const xOverlap = !(a.x + a.width <= b.x || b.x + b.width <= a.x)
        if (!xOverlap) continue
        const gapTop = a.y - (b.y + b.height)
        const gapBottom = b.y - (a.y + a.height)
        if (gapTop >= 0 && gapTop < a.height * 0.6) {
          py = Math.max(py, a.y - Math.floor(gapTop * 0.3))
          ph = a.height + (a.y - py)
        }
        if (gapBottom >= 0 && gapBottom < a.height * 0.6) {
          ph = Math.min(h - py, ph + Math.floor(gapBottom * 0.3))
        }
      }

      padded.push({
        x: Math.round(px * ratioW),
        y: Math.round(py * ratioH),
        width: Math.round(pw * ratioW),
        height: Math.round(ph * ratioH),
        lineNumber: padded.length + 1,
      })
    }

    padded.sort((a, b) => {
      const yDiff = a.y - b.y
      if (Math.abs(yDiff) < Math.min(a.height, b.height) * 0.5) {
        return a.x - b.x
      }
      return yDiff
    })

    let lineNum = 0
    let lastY = -999
    for (const box of padded) {
      if (Math.abs(box.y - lastY) > Math.max(4, box.height * 0.3)) {
        lineNum++
        lastY = box.y
      }
      box.lineNumber = lineNum
    }

    if ((window as any).__OCR_DEBUG__) {
      console.log(`[DB Detector] ${padded.length} boxes after unclip (candidates: ${candidates.length}, scored: ${scored.length})`)
    }

    return padded
  }

  dispose(): void {
    if (this.session) {
      this.session.release()
    }
    this.session = null
    this.initialized = false
  }
}

function labelConnectedComponents(bitmap: Uint8Array, w: number, h: number): Int32Array {
  const labels = new Int32Array(w * h)
  let nextLabel = 1
  const equiv: number[] = [0]

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const idx = y * w + x
      if (!bitmap[idx]) continue

      const left = x > 0 ? labels[y * w + (x - 1)]! : 0
      const up = y > 0 ? labels[(y - 1) * w + x]! : 0

      if (left === 0 && up === 0) {
        labels[idx] = nextLabel
        equiv[nextLabel] = nextLabel
        nextLabel++
      } else if (left !== 0 && up === 0) {
        labels[idx] = left
      } else if (left === 0 && up !== 0) {
        labels[idx] = up
      } else {
        const root = Math.min(left, up)
        const other = Math.max(left, up)
        labels[idx] = root
        if (root !== other) {
          equiv[other] = findRoot(equiv, root)
          unionRoots(equiv, root, other)
        }
      }
    }
  }

  for (let i = 0; i < labels.length; i++) {
    if (labels[i] !== 0) {
      labels[i] = findRoot(equiv, labels[i]!)
    }
  }

  const remap: number[] = [0]
  let cur = 1
  for (let i = 0; i < labels.length; i++) {
    const lbl = labels[i]!
    if (lbl === 0) continue
    if (!remap[lbl]) {
      remap[lbl] = cur
      cur++
    }
    labels[i] = remap[lbl]!
  }

  return labels
}

function findRoot(equiv: number[], x: number): number {
  while (equiv[x] !== x) {
    equiv[x] = equiv[equiv[x]!]!
    x = equiv[x]!
  }
  return x
}

function unionRoots(equiv: number[], a: number, b: number): void {
  const ra = findRoot(equiv, a)
  const rb = findRoot(equiv, b)
  if (ra !== rb) {
    equiv[rb] = ra
  }
}
